import * as React from 'react';
import * as _ from 'lodash';
import * as styles from '../support.module.scss';
import TertiaryButton from '../../common/tertiary-button';
import SecondaryButton from '../../common/secondary-button';
import FilterSection from './filter-section';
import FilterType from '../../../models/enums/filterType';
import useSupport from '../../../hooks/useSupport';
import { SupportContext } from '../../../shared/support-context';
import { AppContext } from '../../../shared/app-context';
import MaterialIcon from '@material/react-material-icon';
import useMeasuredRef from '../../../hooks/useMeasuredRef';
import Scrollbars from 'react-custom-scrollbars';

interface Props {
  collapsed: boolean;
}

/**
 * SupportSidebar
 * @function SupportSidebar - Sidebar on the support page used for filters
 */
const SupportSidebar = ({ collapsed }: Props) => {
  const { state } = React.useContext(AppContext);
  const {
    allFilters,
    localSelectedFilters,
    selectedFilters,
    orderColumn,
    searchQuery,
    dispatch,
    pageSize,
    orderDirection
  } = React.useContext(SupportContext);
  const { fetchSupportTickets, clearAllFilters, countFilters } = useSupport();
  const { measuredRef, dimensions } = useMeasuredRef([], true);

  return (
    <React.Fragment>
      <div className={styles.topSection}>
        <div className={collapsed ? styles.collapsedIconContainer : styles.iconContainer}>
          <MaterialIcon icon="filter_list" />
          {!collapsed && <span>Filters</span>}
        </div>
        {!collapsed && (
          <div className={collapsed ? styles.collapsedClear : styles.clearSection}>
            <TertiaryButton text="Clear All" title="Clear All" handleClick={() => clearAllFilters(state.clientId)} />
            <span className={styles.filterCount}>{countFilters()}</span>
          </div>
        )}
      </div>
      <div className={collapsed ? styles.collapsedFilters : styles.filters}>
        <div className={styles.filterButtons}>
          <TertiaryButton
            text="Cancel"
            title="Cancel"
            deactive={_.isEqual(localSelectedFilters, selectedFilters)}
            handleClick={() => dispatch({ localSelectedFilters: selectedFilters })}
          />
          <SecondaryButton
            text="Apply"
            title="Apply Filters"
            styles={{ marginLeft: 18 }}
            deactive={
              !!(
                (localSelectedFilters.createdDateStart && !localSelectedFilters.createdDateStart._isValid) ||
                (localSelectedFilters.createdDateEnd && !localSelectedFilters.createdDateEnd._isValid)
              ) || _.isEqual(localSelectedFilters, selectedFilters)
            }
            handleClick={() => {
              dispatch({ selectedFilters: localSelectedFilters });
              fetchSupportTickets(state.clientId, {
                orderColumn,
                orderDirection,
                searchTerm: searchQuery,
                startAt: 0,
                count: pageSize
              });
            }}
          />
        </div>
        <form ref={measuredRef} style={{ height: `calc(100vh - ${dimensions ? 20 + dimensions.top : 0}px` }}>
          <Scrollbars className={styles.scrollTable} autoHide={true} universal={true}>
            <FilterSection
              filterType={FilterType.Type}
              filters={allFilters.types}
              localFilters={localSelectedFilters.requestTypeSelected}
            />
            <FilterSection
              filterType={FilterType.Status}
              filters={allFilters.statuses}
              localFilters={localSelectedFilters.statusSelected}
            />
            <FilterSection
              filterType={FilterType.Requester}
              filters={allFilters.requesters}
              localFilters={localSelectedFilters.requesterSelected}
            />
            <FilterSection
              filterType={FilterType.Organization}
              filters={allFilters.organizations}
              localFilters={localSelectedFilters.organizationSelected}
            />
            <FilterSection filterType={FilterType.CreatedDate} />
          </Scrollbars>
        </form>
      </div>
    </React.Fragment>
  );
};

export default SupportSidebar;
